import { useIntl } from 'react-intl'
import { useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { shouldHideProgressTracking } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'

import { actions, mixpanelEvents } from '../../../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../../../mixpanel/mixpanelTracking'
import {
  CLIENT_ACTIVITIES,
  CLIENT_DOCUMENTS,
  CLIENT_EVALUATION,
  CLIENT_HOME,
  CLIENT_MEDICATIONS,
  CLIENT_NOTES,
  CLIENT_PROGRESS,
  CLIENTS_SESSIONS,
} from '../../../../../common/constants/routingConstants'
import { getAuthConfig } from '../../../../../data/auth/authSelectors'
import { useNpTestPlanEntrypoint } from '../../../../npTestPlans/hooks/useNpTestPlanEntrypoint'

export const useTabsConfig = (currentEpisode?: Episode) => {
  const { formatMessage } = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  const config = useSelector(getAuthConfig)
  const { isClientHeaderRedesignEnabled } = useFlags()
  const { isVisible: isEvaluationTabVisible } = useNpTestPlanEntrypoint(currentEpisode)
  const isProgressTabVisible = !shouldHideProgressTracking(currentEpisode?.program_name)

  return [
    {
      displayName: isClientHeaderRedesignEnabled
        ? formatMessage({
            defaultMessage: 'Overview',
            description: 'Navigation tab label for the client`s overview page',
          })
        : formatMessage({
            defaultMessage: 'Summary',
            description: 'Navigation tab label for the client`s summary page',
          }),
      onTabPressed: () => {
        navigate(CLIENT_HOME.route)
      },
      isActive: location.pathname === CLIENT_HOME.route,
    },
    {
      displayName: isClientHeaderRedesignEnabled
        ? formatMessage({
            defaultMessage: 'Care timeline',
            description: 'Navigation tab label for the client`s care timeline page',
          })
        : formatMessage({
            defaultMessage: 'Sessions',
            description: 'Navigation tab label for the client`s session page',
          }),
      onTabPressed: () => {
        navigate(CLIENTS_SESSIONS.route)
      },
      isActive: location.pathname === CLIENTS_SESSIONS.route,
    },
    ...(isEvaluationTabVisible
      ? [
          {
            displayName: formatMessage({
              defaultMessage: 'Evaluation',
              description: 'Navigation tab label for the client`s neuropsych evaluation page',
            }),
            onTabPressed: () => {
              track({
                event: mixpanelEvents.BUTTON_PRESS,
                action: actions.OPEN_CLIENT_EVALUATION_TAB,
                page: CLIENT_HOME.route,
              })
              navigate(CLIENT_EVALUATION.route)
            },
            isActive: location.pathname === CLIENT_EVALUATION.route,
          },
        ]
      : []),
    ...(isProgressTabVisible
      ? [
          {
            displayName: formatMessage({
              defaultMessage: 'Progress',
              description: 'Navigation tab label for the client`s progress page',
            }),
            onTabPressed: () => {
              navigate(CLIENT_PROGRESS.route)
            },
            isActive: location.pathname === CLIENT_PROGRESS.route,
          },
        ]
      : []),
    {
      displayName: formatMessage({
        defaultMessage: 'Activities',
        description: 'Activities tab label for the client`s notes page',
      }),
      onTabPressed: () => {
        navigate(CLIENT_ACTIVITIES.route)
      },
      isActive: location.pathname === CLIENT_ACTIVITIES.route,
    },
    {
      displayName: formatMessage({
        defaultMessage: 'Documents',
        description: 'Navigation tab label for the client`s documents page',
      }),
      onTabPressed: () => {
        track({
          event: mixpanelEvents.BUTTON_PRESS,
          action: actions.OPEN_CLIENT_DOCUMENTS_TAB,
          page: CLIENT_HOME.route,
        })
        navigate(CLIENT_DOCUMENTS.route)
      },
      isActive: location.pathname === CLIENT_DOCUMENTS.route,
    },
    ...(!isClientHeaderRedesignEnabled
      ? [
          {
            displayName: formatMessage({
              defaultMessage: 'Notes',
              description: 'Navigation tab label for the client`s notes page',
            }),
            onTabPressed: () => {
              navigate(CLIENT_NOTES.route)
            },
            isActive: location.pathname === CLIENT_NOTES.route,
          },
        ]
      : []),
    ...(config?.medicationEnabled
      ? [
          {
            displayName: formatMessage({
              defaultMessage: 'Medications',
              description: 'Navigation tab label for the client`s medication page',
            }),
            onTabPressed: () => {
              navigate(CLIENT_MEDICATIONS.route)
            },
            isActive: location.pathname === CLIENT_MEDICATIONS.route,
          },
        ]
      : []),
  ]
}
